import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import Router, { useRouter } from 'next/router'
import React, { useEffect, useRef, useState } from 'react'
import {
  OnDemandCity,
  OnDemandCityConfig
} from 'src/constants/onDemandCoverage'
import { useFilterProvider } from 'src/context/FilterProvider'
import useBreakpoints from 'src/hooks/useBreakpoints'
import useLazyNamespace from 'src/hooks/useLazyNamespace'
import { numberWithCommas } from 'src/utils'
import { afterNextPaint } from 'src/utils/afterNextPaint'
import { buildOnDemandHref, getOnDemandImage } from 'src/utils/onDemand'
import {
  onDemandCardClick,
  onDemandCardView,
  onDemandWhatsapp
} from 'src/utils/pixels/spotmeEvents'
import { buildWhatsappUrl } from 'src/utils/whatsapp'

import { ON_DEMAND_SCROLL_KEY } from './OnDemandSearchCard'

interface OnDemandRecoveryCardProps {
  /** Canonical on-demand city slug (e.g. 'monterrey'). The primary CTA points
   * to `/renta-mini-bodegas/almacenamiento-a-domicilio/{canonicalCity}`. */
  canonicalCity: OnDemandCity
  /** Resolved coverage config — used for the human-readable display name in
   * the headline ("Monterrey", "Ciudad de México"). */
  cityConfig: OnDemandCityConfig
}

/**
 * Hero-sized empty-state recovery card for the SelfStorage search results
 * grid. Rendered ABOVE `MarketNotAvailable` / `CaptureLead` when the current
 * city resolves to a covered, partner-active on-demand market AND the search
 * returned 0 results. (For sparse 1-7 results, the inline
 * `<OnDemandSearchCard>` ships in the grid and this recovery card is
 * suppressed by `EmptyState`.)
 *
 * Mobile/desktop nav split mirrors `LocationSearchCard` and the inline
 * `<OnDemandSearchCard>`:
 *   - **Mobile**: primary CTA uses an onClick that `Router.replace`s the
 *     search URL with `sliderOpen` + `scrollY=on-demand-card`, then
 *     `Router.push`es to the landing page. Back-nav restores drawer + scroll.
 *   - **Desktop**: primary CTA opens the landing page in a new tab via
 *     `<Link target="_blank">`. The original search tab keeps its state.
 *
 * The WhatsApp secondary CTA always opens in a new tab regardless of
 * breakpoint — chat is independent of search context. Tapping it fires
 * `storage_on_demand_whatsapp` so the alternative-conversion path is
 * trackable in GA4 / Google Ads.
 */
const OnDemandRecoveryCard = ({
  canonicalCity,
  cityConfig
}: OnDemandRecoveryCardProps) => {
  const { t } = useTranslation('page_mini_bodega')
  // `page_mini_bodega` is dropped from non-storage search pages'
  // serverSideTranslations (~37 kB). This card only renders on SelfStorage,
  // where the pillar namespace already loads it — so this lazy-load is a
  // safety net and satisfies the i18n coverage linter (which is
  // useLazyNamespace-aware). See docs/performance/large-page-data.md.
  useLazyNamespace('page_mini_bodega')
  const router = useRouter()
  const { shallowParams, isSliderOpen, isReady } = useFilterProvider()
  const { isDownMd: rawIsDownMd, isUpMd } = useBreakpoints()
  const ref = useRef<HTMLElement>(null)
  const [isClicked, setIsClicked] = useState(false)
  // SSR-safe breakpoint: MUI's useMediaQuery returns false on the server,
  // so on the first client render (before this effect runs) it returns the
  // real viewport value. Gate the render-time CTA branch on `mounted` so
  // SSR and first hydrate always pick the desktop (Link) branch — React
  // #418 otherwise when this card mounts in mobile search results.
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])
  const isDownMd = mounted && rawIsDownMd

  // Bare path until mount — same hydration fix as OnDemandSearchCard.
  const href = buildOnDemandHref(
    canonicalCity,
    mounted ? router.asPath : router.asPath.split(/[?#]/)[0]
  )
  const title = t('CROSS_LINK_ON_DEMAND.TITLE_CITY', {
    city: cityConfig.displayName
  })
  const whatsappUrl = buildWhatsappUrl(
    t('CROSS_LINK_ON_DEMAND.WHATSAPP_MSG_CITY', {
      city: cityConfig.displayName
    })
  )

  // Return-nav scroll restoration. Same pattern as the inline
  // `OnDemandSearchCard` and `LocationSearchCard`.
  useEffect(() => {
    if (!isReady || !isDownMd) return
    if (!isSliderOpen) return
    if (!ref.current) return
    if (isClicked) return

    const isInViewWindow = ref.current.getBoundingClientRect().top < 0
    if (isInViewWindow) return

    if (shallowParams.scrollY === ON_DEMAND_SCROLL_KEY) {
      ref.current.scrollIntoView({ behavior: 'instant', block: 'center' })
    }
  }, [isClicked, isDownMd, isReady, isSliderOpen, shallowParams])

  // Discovery-funnel impression (FUNNELS.md F6) — same viewport-gated +
  // session-deduped contract as the inline OnDemandSearchCard; see the
  // rationale on `onDemandCardView`.
  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        onDemandCardView({ city: canonicalCity, surface: 'empty_state' })
        observer.disconnect()
      },
      { threshold: 0.5 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [canonicalCity])

  const onPrimaryClick = async () => {
    if (isUpMd) return
    onDemandCardClick({ city: canonicalCity, surface: 'empty_state' })
    setIsClicked(true)
    let target = href
    if (typeof window !== 'undefined') {
      const currentUrl = new URL(window.location.href)
      const sliderDesired = isSliderOpen ? 'true' : 'false'
      let dirty = false
      if (currentUrl.searchParams.get('sliderOpen') !== sliderDesired) {
        currentUrl.searchParams.set('sliderOpen', sliderDesired)
        dirty = true
      }
      if (currentUrl.searchParams.get('scrollY') !== ON_DEMAND_SCROLL_KEY) {
        currentUrl.searchParams.set('scrollY', ON_DEMAND_SCROLL_KEY)
        dirty = true
      }
      if (dirty) {
        // Paint the tap first — the ad SDKs' `history` hooks make this
        // replace ~156 ms at 4× CPU (see src/utils/afterNextPaint.ts).
        await afterNextPaint()
        await Router.replace(
          `${currentUrl.pathname}${currentUrl.search}`,
          undefined,
          { shallow: true, scroll: false }
        )
      }
      // Rebuild target with the just-updated source URL captured in
      // `searchReturn` so the breadcrumb back link on the landing page
      // restores drawer + scroll. See OnDemandSearchCard.onClick for the
      // full rationale.
      const returnPath = `${currentUrl.pathname}${currentUrl.search}`
      target = buildOnDemandHref(canonicalCity, returnPath)
    }
    Router.push(target)
  }

  const handleWhatsappClick = () => {
    onDemandWhatsapp({ city: canonicalCity })
  }

  // `w-full` + `whitespace-nowrap` below `@md` is load-bearing, not cosmetic:
  // the rail is ~357px at a 900px viewport, and a `w-fit` pill there wrapped
  // "Cotiza tu espacio" onto two lines with the chevron stranded at the edge.
  // Full-width is also the right affordance in a single-column card.
  const primaryCtaClass =
    'inline-flex w-full items-center justify-center gap-2 rounded-full bg-blue-500 px-6 py-3 text-base font-medium whitespace-nowrap text-white-500 transition-transform duration-200 hover:translate-x-0.5 @md:w-auto @md:px-7'

  // Mobile primary CTA = button with onClick (no Link). Desktop primary CTA
  // = <Link target="_blank">. The two branches share the same className for
  // visual parity.
  const primaryCta = isDownMd ? (
    <button
      type="button"
      onClick={onPrimaryClick}
      className={`${primaryCtaClass} cursor-pointer`}
    >
      <span>{t('CROSS_LINK_ON_DEMAND.CTA')}</span>
      <i className="si si-right text-[10px]" aria-hidden="true" />
    </button>
  ) : (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={primaryCtaClass}
      onClick={() =>
        onDemandCardClick({ city: canonicalCity, surface: 'empty_state' })
      }
    >
      <span>{t('CROSS_LINK_ON_DEMAND.CTA')}</span>
      <i className="si si-right text-[10px]" aria-hidden="true" />
    </Link>
  )

  return (
    <section
      ref={ref}
      // `@container` — NOT the viewport `md:` breakpoints this card used to
      // key off. It only ever renders inside `<EmptyState>` → `<SearchList>`,
      // i.e. the 45% desktop rail or the mobile drawer, so its box is ~357px
      // at a 900px viewport — NARROWER than a 428px phone. Viewport `md:`
      // therefore applied the widest treatment (32px headline, p-10, a 5/7
      // side-by-side grid) at the card's tightest width: the headline broke
      // over seven lines and the CTA wrapped. Every size step below queries
      // the card's own width instead. Do not convert these back to `md:`.
      className="@container w-full overflow-hidden rounded-2xl border border-blue-500/20 bg-cream-300"
      data-testid="on-demand-recovery-card"
      aria-labelledby="on-demand-recovery-heading"
    >
      {/* Single stacked shape at every width. The old `md:grid-cols-12` 5/7
          split never had room in this rail; one shape removes the whole
          reflow class of bugs. The banner flattens as the card widens so a
          1920px rail doesn't get a 400px-tall photo. */}
      <div className="relative aspect-16/10 w-full overflow-hidden bg-blue-500/10 @sm:aspect-2/1 @xl:aspect-5/2">
        <Image
          src={getOnDemandImage(canonicalCity)}
          alt={t('CROSS_LINK_ON_DEMAND.EYEBROW')}
          width={760}
          height={475}
          sizes="(min-width: 900px) 45vw, 100vw"
          className="h-full w-full object-cover object-[center_30%]"
          loading="lazy"
        />
        {/* Eyebrow rides the photo instead of taking a text row in a column
            this narrow — and gives the card a silhouette the white listing
            cards below don't have. Same ink/blur treatment as the urgency
            pill on `LocationSearchCard`. */}
        <p className="absolute bottom-3 left-3 rounded-full bg-ink-700/85 px-3 py-1 font-display text-[10px] tracking-[0.16em] text-white-500 uppercase backdrop-blur-sm @sm:text-[11px]">
          {t('CROSS_LINK_ON_DEMAND.EYEBROW')}
        </p>
      </div>

      {/* Text + CTA cluster */}
      <div className="flex flex-col gap-4 p-5 @sm:gap-5 @sm:p-6 @xl:p-8">
        <h2
          id="on-demand-recovery-heading"
          className="font-display text-[22px] leading-[1.15] font-medium text-ink-700 @sm:text-[26px] @xl:text-[30px]"
        >
          {title}
        </h2>
        <p className="max-w-140 text-[14px] leading-relaxed text-ink-200 @sm:text-[15px]">
          {t('CROSS_LINK_ON_DEMAND.BODY')}
        </p>
        {/* Price — a hairline row, not a bordered chip. A white box inside a
            cream card is chrome-on-chrome at this size; the house rule is
            hairlines over stacked card frames. Italic Fraunces numeral stays,
            it's the signature. */}
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-t border-ink-100/15 pt-4">
          <span className="font-display text-[20px] leading-none font-medium tracking-[-0.01em] text-blue-500 italic @sm:text-[24px]">
            {t('CROSS_LINK_ON_DEMAND.PRICE_FROM', {
              price: numberWithCommas(cityConfig.perSqmRates.twelveMonths)
            })}
          </span>
          <span className="text-[12px] leading-tight text-ink-200 @sm:text-[13px]">
            {t('CROSS_LINK_ON_DEMAND.PRICE_NOTE')}
          </span>
        </div>
        {/* Stack-until-there-is-room. `@md` (448px card) rather than the old
            `sm:` (428px VIEWPORT) — same intent as CLAUDE.md's "stack until
            md:" rule, measured against the box that actually holds these two. */}
        <div className="flex flex-col items-stretch gap-3 @md:flex-row @md:items-center">
          {primaryCta}
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleWhatsappClick}
            className="inline-flex items-center justify-center gap-2 text-sm font-medium text-blue-500 underline-offset-4 transition-colors hover:underline"
          >
            <i className="si si-chat text-[12px]" aria-hidden="true" />
            <span>WhatsApp</span>
          </a>
        </div>
      </div>
    </section>
  )
}

export default React.memo(OnDemandRecoveryCard)
